import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  AlertController,
  Events,
  LoadingController,
  ModalController,
  NavController,
  NavParams,
} from "ionic-angular";
import VolunteerPreferences from "models/resource-management/volunteers/volunteer-preferences";
import { VolunteerSkillInterest } from "models/resource-management/volunteers/volunteer-skill-interest";
import moment from "moment";
import { Subscription } from "rxjs";
import { AccessControlService } from "../../../app/access-control/access-control-service";
import { AuthService } from "../../../app/auth/auth.service";
import CompanyResource from "../../../models/company-resources/companyResource";
import EmployeeDetail from "../../../models/company-resources/employee-detail";
import LookupValue from "../../../models/patient-chart/lookupValue";
import CompanyResourceType from "../../../models/resource-management/company-resource-type.enum";
import CredentialedResource from "../../../models/resource-management/credentialed-resource";
import { RadiusMeasurement } from "../../../models/resource-management/volunteers/radius-measurement";
import Volunteer from "../../../models/resource-management/volunteers/volunteer";
import VolunteerAvailabilityPreference from "../../../models/resource-management/volunteers/volunteer-availability-preference";
import VolunteerLocationPreference from "../../../models/resource-management/volunteers/volunteer-location-preference";
import VolunteerSchedule from "../../../models/resource-management/volunteers/volunteer-schedule";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { PatientChartService } from "../../../providers/patient-charts/patient-chart.service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { UsersService } from "../../../providers/users-service/users.service";
import CompanyResourcePage from "../company-resource-page";
import { AccountContactDetails } from "../contact-details/account/account-contact-details";
import { EmployeeHealthContactDetails } from "../contact-details/employee-health/employee-health-contact-details";
import { GeneralContactDetails } from "../contact-details/general/general-contact-details";
import { JobRoleContactDetails } from "../contact-details/job-role/job-role-contact-details";
import { ScheduleContactDetails } from "../contact-details/schedule/schedule-contact-details";
import { SkillsAndInterestsContactDetails } from "../contact-details/skills-and-interests/skills-and-interests-contact-details";

@Component({
  selector: "company-resources-contact",
  templateUrl: "company-resources-contact.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyResourcesContactPage
  extends CompanyResourcePage
  implements OnDestroy
{
  @ViewChild(GeneralContactDetails) generalChild: GeneralContactDetails;
  @ViewChild(JobRoleContactDetails) jobRoleChild: JobRoleContactDetails;
  @ViewChild(EmployeeHealthContactDetails)
  employeeHealthChild: EmployeeHealthContactDetails;
  @ViewChild(AccountContactDetails) accountChild: AccountContactDetails;
  @ViewChild(SkillsAndInterestsContactDetails)
  skillsAndInterestsChild: SkillsAndInterestsContactDetails;
  @ViewChild(ScheduleContactDetails) scheduleChild: ScheduleContactDetails;

  contactData: CredentialedResource;
  _displayName: string;
  parentForm: FormGroup;
  private _isAdminstrator: boolean = false;
  _saveEnabled: boolean = false;
  _contactIsEmployee: boolean = false;
  _contactIsSystemUser: boolean = false;
  _contactIsVolunteer: boolean = false;
  subscriptions: Subscription[] = [];
  contactType: CompanyResourceType;
  _volunteerTypes: LookupValue[];
  _professionalRelations: LookupValue[];

  _isEditMode: boolean = false;
  _isNew: boolean = false;

  tabs = [
    {
      id: "generalDetails",
      title: "General Details",
      selected: true,
      status: "",
    },
    {
      id: "jobRole",
      title: "Job Role",
      selected: false,
      status: "",
    },
  ];
  activeTab = this.tabs[0];
  activeTabId = this.activeTab.id;
  deletedAddresses = [];
  deletedPhoneNumbers = [];
  deletedCredentials = [];

  jobRoleInitData = {
    credentials: [],
  };
  days = [
    "MONDAY",
    "TUESDAY",
    "WEDNESDAY",
    "THURSDAY",
    "FRIDAY",
    "SATURDAY",
    "SUNDAY",
  ];

  public lookupValues: any[] = [];

  private getLookupValues() {
    this.resourceManagementService
      .getLookUpValues()
      .pipe()
      .take(1)
      .subscribe((vals: any[]) => {
        this.lookupValues = vals;
      });
  }

  get isAdministrator(): boolean {
    return this._isAdminstrator;
  }

  set isAdministrator(v: boolean) {
    this._isAdminstrator = v;
  }

  get saveEnabled() {
    return this._saveEnabled;
  }

  set saveEnabled(value: boolean) {
    this._saveEnabled = value;
  }

  get isEditMode() {
    return this._isEditMode;
  }

  set isNew(value: boolean) {
    this._isNew = value;
  }

  get isNew() {
    return this._isNew;
  }

  set isEditMode(value: boolean) {
    this._isEditMode = value;
    this.checkCanSave();
  }

  get contactUserId(): string {
    if (!this.contactData) {
      return "";
    }
    return this.contactData.userId;
  }

  get contactIsEmployee() {
    return this._contactIsEmployee;
  }

  set contactIsEmployee(value: boolean) {
    this._contactIsEmployee = value;
    this.updateTabs();
  }

  get contactIsSystemUser() {
    return this._contactIsSystemUser;
  }

  set contactIsSystemUser(value: boolean) {
    this._contactIsSystemUser = value;
    this.contactIsSystemUser
      ? this.addAccountFormControl()
      : this.removeAccountFormControl();
    this.updateTabs();
  }

  get contactIsVolunteer() {
    return this._contactIsVolunteer;
  }

  set contactIsVolunteer(value: boolean) {
    this._contactIsVolunteer = value;
    this.updateTabs();
  }

  get generalDetailsControl() {
    if (this.parentForm.contains("generalDetails")) {
      return this.parentForm.get("generalDetails") as FormControl;
    }
    return null;
  }

  get jobRoleControl() {
    if (this.parentForm.contains("jobRole")) {
      return this.parentForm.get("jobRole") as FormControl;
    }
    return null;
  }

  get employeeHealthControl() {
    if (this.parentForm.contains("employeeHealth")) {
      return this.parentForm.get("employeeHealth") as FormControl;
    }
    return null;
  }

  get accountControl() {
    if (this.parentForm.contains("account")) {
      return this.parentForm.get("account") as FormControl;
    }
  }

  get skillsAndInterestsControl() {
    if (this.parentForm.contains("skillsAndInterests")) {
      return this.parentForm.get("skillsAndInterests") as FormControl;
    }
  }

  get schedulesControl() {
    if (this.parentForm.contains("schedules")) {
      return this.parentForm.get("schedules") as FormControl;
    }
  }

  get displayName() {
    return this._displayName;
  }

  set displayName(value: string) {
    this._displayName = value;
  }

  constructor(
    protected accessControlService: AccessControlService,
    protected navBarSvc: NavBarServiceProvider,
    protected navCtrl: NavController,
    protected navParams: NavParams,
    protected modalController: ModalController,
    protected resourceManagementService: ResourceManagementService,
    protected loader: LoadingController,
    protected userService: UsersService,
    protected stateManagementService: StateManagementService,
    protected patientChartService: PatientChartService,
    protected fb: FormBuilder,
    protected alertController: AlertController,
    protected events: Events,
    protected authService: AuthService,
    private authSvc: AuthService
  ) {
    super(
      accessControlService,
      navBarSvc,
      navCtrl,
      navParams,
      userService,
      stateManagementService,
      patientChartService,
      resourceManagementService
    );

    if (this.navParams && this.navParams.data) {
      if (this.navParams.data.provider) {
        this.contactData = this.navParams.data.provider;
        this.contactType = +this.navParams.data.provider.resourceType;
      } else if (this.navParams.data.contactType) {
        this.contactType = +this.navParams.data.contactType;
      }
      if (this.navParams.data.volunteerTypes) {
        this._volunteerTypes = this.navParams.data.volunteerTypes;
      }
      if (this.navParams.data.professionalRelations) {
        this._professionalRelations = this.navParams.data.professionalRelations;
      }
      this._isNew = !!this.navParams.data.isNew;
      if (this._isNew) {
        this.resourceManagementService.changeEmailAddress("");
      }
    }
    this.parentForm = this.fb.group({
      id: [""],
      generalDetails: [null, Validators.required],
      jobRole: [this.jobRoleInitData, Validators.required],
      skillsAndInterests: [],
      schedules: [],
      employeeHealth: [],
    });

    this.getLookupValues();

    this.subscriptions.push(
      this.parentForm.valueChanges.subscribe(this.onFormChanges)
    );

    this.loadData(this.contactData);
    if (!this.parentForm.value.id) {
      this.isEditMode = true;
    }
  }

  onFormChanges = (value) => {
    this.contactIsEmployee =
      value.generalDetails &&
      value.generalDetails.professionalRelation &&
      value.generalDetails.professionalRelation === 2;
    this.contactType = +value.generalDetails.resourceType;
    this.contactIsVolunteer = value.generalDetails && this.contactType === 5;

    if (this.contactData === undefined) {
      this.contactIsSystemUser =
        value.generalDetails && value.generalDetails.systemUser === true;
    }

    this.buildDisplayName(value);
    this.checkCanSave();
    this.updateTabStatus();
  };

  buildDisplayName(value) {
    if (value.generalDetails) {
      const { lastName, firstName, middleInitial, preferredName } =
        value.generalDetails;

      let displayName = [];
      if (lastName) {
        displayName.push(lastName + (firstName ? "," : ""));
      }
      if (firstName) {
        displayName.push(firstName);
      }
      if (middleInitial) {
        displayName.push(`${middleInitial}.`);
      }
      if (preferredName) {
        displayName.push(`"${preferredName}"`);
      }
      this.displayName = displayName.join(" ");
    }
  }

  ionViewCanEnter() {
    return this.authSvc.authenticated();
  }

  ionViewDidLoad() {
    this.evaluateAccess();
    this.stateManagementService.setAssessmentType("Resource Management");
    // const user = this.authSvc.getUser();
    // const role = JSON.parse(user.profile.role);
    // const { isAdministrator } = role[0];
    // this.isAdministrator = isAdministrator;
    // this.isAdministrator = true;    //CHECK WHICH PERMISSION IS NEEDED
  }

  checkCanSave() {
    const generalValid =
      this.generalDetailsControl && this.generalDetailsControl.valid;
    const jobRoleValid = this.jobRoleControl && this.jobRoleControl.valid;
    const employeeHealthValid = this.contactIsEmployee
      ? this.employeeHealthControl && this.employeeHealthControl.valid
      : true;
    let allValid = generalValid && jobRoleValid && employeeHealthValid;
    if (this.contactIsSystemUser && this.isAdministrator) {
      const accountValid = this.accountControl && this.accountControl.valid;
      allValid = allValid && accountValid;
    }
    if (this.contactIsVolunteer) {
      const skillsAndInterestsValid =
        this.skillsAndInterestsControl && this.skillsAndInterestsControl.valid;
      const scheduleValid =
        this.schedulesControl && this.schedulesControl.valid;
      allValid = allValid && skillsAndInterestsValid && scheduleValid;
    }

    this.saveEnabled = allValid;
  }

  loadData(data: CredentialedResource) {
    if (data) {
      if (data.employeeInformation) {
        this.flattenEmployeeContactData(data);
      }

      this.parentForm.patchValue({
        generalDetails: {
          ...data,
          systemUser: data.isAUser,
          gender: data.gender,
          dateOfBirth: data.dateOfBirth,
          primaryLanguage: data.primaryLanguage,
        },
      });
      this.parentForm.patchValue({ jobRole: { ...data } });
      this.parentForm.patchValue({ id: data.id });
      this.contactIsSystemUser = data.isAUser;

      if (data instanceof Volunteer) {
        if (data.preferences) {
          let otherTerm = null;
          let ski = data.preferences.skillsAndInterests;
          if (ski) {
            for (let sIndex = 0; sIndex < ski.length; sIndex++) {
              if (ski[sIndex].term === "Other" && ski[sIndex].other != null) {
                otherTerm = ski[sIndex].other;
                ski.splice(sIndex, 1);
              }
            }
          }
          const skillsAndInterests = data.preferences.skillsAndInterests.map(
            (ski) => ski.term
          );

          this.parentForm.patchValue({
            skillsAndInterests: {
              skillsAndInterests: skillsAndInterests,
              languages: data.languages,
              other: otherTerm,
            },
          });
          let schedules = this.mapScheduleFromScheduleData(
            data.preferences.schedule
          );
          this.parentForm.patchValue({
            schedules: { schedules: schedules },
          });

          let volunteerTypes = this.unMaskVolunteerType(data.volunteerType);
          this.parentForm.patchValue({
            jobRole: {
              ...data,
              volunteerType: volunteerTypes,
              trainingCompletionDate: data.trainingCompletionDate,
            },
          });
        }
      }

      if (data.employeeInformation && data.employeeInformation.healthDetails) {
        this.parentForm.patchValue({
          employeeHealth: {
            vaccinations: data.employeeInformation.healthDetails,
          },
        });
      }
      this.recheckJobRole();
    } else {
      this.parentForm.patchValue({
        generalDetails: { resourceType: this.contactType },
      });
    }
  }

  toggleIsEditMode() {
    this._isEditMode = !this._isEditMode;
    this.recheckJobRole();
  }

  close() {
    this.navCtrl.pop();
  }

  cancel() {
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present();
  }

  showAlert = ({ title, message }) => {
    let alert = this.alertController.create({
      title,
      message,
      buttons: [
        {
          text: "Ok",
          handler: () => {},
        },
      ],
    });
    alert.present();
  };

  save() {
    this.checkCanSave();
    if (this.saveEnabled) {
      let obj = this.mapCompanyResourceFromForm(this.parentForm.value);

      if (this.parentForm.value.id > 0) {
        this.update(obj);
      } else {
        this.create(obj);
      }
    } else {
      this.showAlert({
        title: "Invalid Submission",
        message: "Missing required fields. Please review.",
      });
    }
  }

  private create(obj) {
    return this.resourceManagementService
      .createProfessionalProvider(obj)
      .pipe()
      .take(1)
      .subscribe((o) => {
        this.onSaveSuccess();
      }, this.onSaveError);
  }

  private update(obj) {
    this.resourceManagementService
      .updateProfessionalProvider(this.contactData.id, obj)
      .pipe()
      .take(1)
      .subscribe(this.onSaveSuccess, this.onSaveError);
  }

  private onSaveSuccess = () => {
    if (this.navParams.data.parent) {
      this.navParams.data.parent.loadProfessionalProviders(0);
    }
    this.navCtrl.pop();
  };

  private onSaveError = (error) => {
    this.showAlert({
      title: "Error",
      message: error.message,
    });
  };

  flattenEmployeeContactData(data): void {
    data.driversLicenseNumber = data.employeeInformation.driversLicenseNumber;
    data.hireDate = data.employeeInformation.hireDate;
    data.employeeAltId = data.employeeInformation.employeeAltId;
    data.driversLicenseExpiration =
      data.employeeInformation.driversLicenseExpiration;
    data.payrollId = data.employeeInformation.payrollId;
    if (data.employeeInformation.jobRoleDetails) {
      data.employmentStatus =
        data.employeeInformation.jobRoleDetails.employmentStatus;
      data.position = data.employeeInformation.jobRoleDetails.position;
      data.department = data.employeeInformation.jobRoleDetails.department;
      data.disciplines = data.employeeInformation.jobRoleDetails.disciplines;
    }
  }

  mapCompanyResourceFromForm(form: any): CompanyResource {
    let cr = new CompanyResource();
    cr.resourceType = +form.generalDetails.resourceType;
    cr.professionalRelation = form.generalDetails.professionalRelation;
    cr.resourceStatus = form.generalDetails.resourceStatus;
    cr.firstName = form.generalDetails.firstName;
    cr.lastName = form.generalDetails.lastName;
    cr.middleInitial = form.generalDetails.middleInitial;
    cr.preferredName = form.generalDetails.preferredName;
    this.generalDetailsTrim(cr);
    cr.emailAddress = form.generalDetails.emailAddress;
    cr.groupAffiliation = form.generalDetails.groupAffiliation;
    cr.addresses = form.generalDetails.addresses.concat(this.deletedAddresses);
    cr.phoneNumbers = form.generalDetails.phoneNumbers.concat(
      this.deletedPhoneNumbers
    );
    cr.notes = form.generalDetails.notes;

    if (form.account) {
      // commented in users branch
      cr.account = form.account;
      // below three lines are added in users branch
      // cr.account = new Account();
      // cr.account.accountName = form.account.accountName;
      // cr.account.workgroups = this.accountChild.assignedWorkGroups;
      cr.account.firstName = cr.firstName;
      cr.account.lastName = cr.lastName;
      cr.userId = this.contactUserId;
    }

    if (form.jobRole) {
      if (this.contactType === 1) {
        cr.npi = form.jobRole.npi;
        cr.dea = form.jobRole.dea;
        cr.taxId = form.jobRole.taxId;
        cr.taxonomyCode = form.jobRole.taxonomyCode;
      } else if (this.contactIsVolunteer) {
        let maskVolunteerType = 0;
        form.jobRole.volunteerType.forEach((value) => {
          maskVolunteerType += value;
        });
        cr.volunteerType = maskVolunteerType;

        cr.trainingCompletionDate = form.jobRole.trainingCompletionDate
          ? moment(form.jobRole.trainingCompletionDate).format()
          : "";

        let schedule: VolunteerSchedule;
        if (form.schedules && form.schedules.schedules) {
          for (let j = 0; j < form.schedules.schedules.length; j++) {
            let availabilities: VolunteerAvailabilityPreference[] = [];
            for (let i = 0; i <= 6; i++) {
              let day: VolunteerAvailabilityPreference = {
                day: "",
                selected: false,
                morning: false,
                afternoon: false,
                evening: false,
              };
              day.day = i.toString();
              let s = form.schedules.schedules[j];
              if (s.morning && s.morningTime.includes(i)) {
                day.morning = true;
                day.selected = true;
              }
              if (s.afternoon && s.afternoonTime.includes(i)) {
                day.afternoon = true;
                day.selected = true;
              }
              if (s.evening && s.eveningTime.includes(i)) {
                day.evening = true;
                day.selected = true;
              }

              availabilities.push(day);
            }
            availabilities = availabilities.filter((a) => a.selected);
            let locationPreferences: VolunteerLocationPreference[] = [];
            locationPreferences.push({
              radius: form.schedules.schedules[j].radius,
              zipCode: form.schedules.schedules[j].zip,
              radiusMeasurement: RadiusMeasurement.MILE,
            });
            schedule = new VolunteerSchedule(
              availabilities,
              locationPreferences
            );
          }
        }

        let skillsAndInterests: VolunteerSkillInterest[] = [];
        if (
          form.skillsAndInterests &&
          form.skillsAndInterests.skillsAndInterests
        ) {
          skillsAndInterests = form.skillsAndInterests.skillsAndInterests.map(
            (ski) => ({
              term: ski,
              selected: true,
              type: "skillInterest",
            })
          );
          cr.languages = form.skillsAndInterests.languages;
        }
        if (form.skillsAndInterests && form.skillsAndInterests.other) {
          let normalize = form.skillsAndInterests.other.split(" ");
          let otherOption = normalize
            .map((word) => {
              return word[0].toUpperCase() + word.substring(1).toLowerCase();
            })
            .join(" ");

          skillsAndInterests.push({
            term: "Other",
            selected: true,
            other: otherOption,
          });
        }

        cr.preferences = new VolunteerPreferences(schedule, skillsAndInterests);
      }
      if (form.jobRole.credentials) {
        cr.credentials = form.jobRole.credentials
          .concat(this.deletedCredentials)
          .map((c) => {
            c.validFromDatetime = c.validFromDatetime
              ? moment.utc(c.validFromDatetime).format()
              : "";
            c.validToDatetime = c.validToDatetime
              ? moment.utc(c.validToDatetime).format()
              : "";
            return c;
          });
      }
      if (form.jobRole.disciplines) {
        let ed = new EmployeeDetail();
        ed.jobRoleDetails = {
          ...ed.jobRoleDetails,
          disciplines: form.jobRole.disciplines
            ? form.jobRole.disciplines.map((d) => {
                d.startDate = d.startDate
                  ? moment.utc(d.startDate).format()
                  : "";
                d.endDate = d.endDate ? moment.utc(d.endDate).format() : "";
                return d;
              })
            : [],
        };

        cr.employeeInformation = ed;
      }
    }
    if (this.contactIsEmployee) {
      let ed = new EmployeeDetail();
      ed.hireDate = form.generalDetails.hireDate
        ? moment(form.generalDetails.hireDate).format("YYYY-MM-DD")
        : "";
      ed.employeeAltId = form.generalDetails.employeeAltId;
      ed.payrollId = form.generalDetails.payrollId;
      ed.driversLicenseNumber = form.generalDetails.driversLicenseNumber;
      ed.driversLicenseExpiration = form.generalDetails.driversLicenseExpiration
        ? moment(form.generalDetails.driversLicenseExpiration).format(
            "YYYY-MM-DD"
          )
        : "";
      ed.jobRoleDetails = {
        employmentStatus: form.jobRole.employmentStatus,
        department: form.jobRole.department,
        position: form.jobRole.position,
        disciplines: form.jobRole.disciplines
          ? form.jobRole.disciplines.map((d) => {
              d.startDate = d.startDate ? moment.utc(d.startDate).format() : "";
              d.endDate = d.endDate ? moment.utc(d.endDate).format() : "";
              return d;
            })
          : [],
      };
      if (form.employeeHealth) {
        ed.healthDetails = form.employeeHealth.vaccinations.map((v) => {
          v.dateGiven = v.dateGiven
            ? moment(v.dateGiven).format("YYYY-MM-DD")
            : "";
          v.dateNextRequired = v.dateNextRequired
            ? moment(v.dateNextRequired).format("YYYY-MM-DD")
            : "";
          v.dateRead = v.dateRead
            ? moment(v.dateRead).format("YYYY-MM-DD")
            : "";
          return v;
        });
      }
      cr.employeeInformation = ed;
    }

    if (this.contactIsEmployee || this.contactIsVolunteer) {
      cr.gender = form.generalDetails.gender;
      cr.dateOfBirth = form.generalDetails.dateOfBirth
        ? moment.utc(form.generalDetails.dateOfBirth).format()
        : "";
      cr.primaryLanguage = form.generalDetails.primaryLanguage;
    }

    return cr;
  }

  private generalDetailsTrim(cr: CompanyResource) {
    cr.firstName = this.trimProperty(cr.firstName);
    cr.lastName = this.trimProperty(cr.lastName);
    if (cr.preferredName) {
      cr.preferredName = this.trimProperty(cr.preferredName);
    }
    if (cr.middleInitial) {
      cr.middleInitial = this.trimProperty(cr.middleInitial);
    }
  }

  private trimProperty(str: string): string {
    return str.replace(/\n/g, "").replace(/\r/g, "").trim();
  }

  mapScheduleFromScheduleData(scheduleData: any) {
    let schedules = [];

    let schedule: any = {
      morning: false,
      afternoon: false,
      evening: false,
      morningTime: [],
      afternoonTime: [],
      eveningTime: [],
      zip:
        scheduleData.locationPreferences.length > 0
          ? scheduleData.locationPreferences[0].zipCode
          : null,
      radius:
        scheduleData.locationPreferences.length > 0
          ? scheduleData.locationPreferences[0].radius
          : null,
    };
    for (let a of scheduleData.availabilities) {
      if (a.morning) {
        schedule.morning = true;
        schedule.morningTime.push(this.days.indexOf(a.day));
      }
      if (a.afternoon) {
        schedule.afternoon = true;
        schedule.afternoonTime.push(this.days.indexOf(a.day));
      }
      if (a.evening) {
        schedule.evening = true;
        schedule.eveningTime.push(this.days.indexOf(a.day));
      }
    }
    schedules.push(schedule);

    return schedules;
  }

  unMaskVolunteerType(volunteerType) {
    let volunteerTypes = [];
    if (volunteerType) {
      for (let i = 0; i < this._volunteerTypes.length; i++) {
        if (volunteerType % 2) {
          volunteerTypes.push(Math.pow(2, i));
        }
        volunteerType = Math.trunc(volunteerType / 2);
      }
    }
    return volunteerTypes;
  }

  // Tab Managemnent
  getTabStatus(control: FormControl) {
    let formContainsValue = control.value
      ? Object.keys(control.value).filter(
          (x) =>
            control.value[x] !== undefined &&
            control.value[x] !== null &&
            control.value[x] !== "" &&
            !(Array.isArray(control.value[x]) && control.value[x].length === 0)
        ).length > 0
      : false;
    if (formContainsValue && control.invalid === true) {
      return "incomplete";
    } else if (formContainsValue && control.valid === true) {
      return "complete";
    } else return "blank";
  }

  updateTabStatus() {
    this.tabs = this.tabs.map((tab) => {
      if (this.parentForm.contains(tab.id)) {
        tab.status = this.getTabStatus(
          this.parentForm.get(tab.id) as FormControl
        );
      }
      return tab;
    });
  }

  selectTab(selectedTab) {
    // update tabs
    this.tabs = this.tabs.map((tab) => {
      tab.selected = tab.id === selectedTab.id;
      return tab;
    });

    // update activeTab key for sections displayed
    this.activeTab = selectedTab;
    this.activeTabId = selectedTab.id;
  }

  updateTabs() {
    // const employeeHealthTabIndex = this.tabs.findIndex(tab => {
    //     return tab.id === 'employeeHealth';
    // });

    // if (this._contactIsEmployee && employeeHealthTabIndex === -1) {
    //     this.tabs.splice(2, 0, {
    //         id: 'employeeHealth',
    //         title: 'Employee Health',
    //         selected: false,
    //         status: '',
    //     });
    // } else if (!this._contactIsEmployee && employeeHealthTabIndex > -1) {
    //     this.tabs.splice(employeeHealthTabIndex, 1);
    // }

    const accountTabIndex = this.tabs.findIndex((tab) => {
      return tab.id === "account";
    });

    if (
      this.contactIsSystemUser &&
      this.isAdministrator &&
      accountTabIndex === -1
    ) {
      this.tabs.push({
        id: "account",
        title: "Account",
        selected: false,
        status: "",
      });
    } else if (!this.contactIsSystemUser && accountTabIndex > -1) {
      this.tabs.splice(accountTabIndex, 1);
    }

    const skillsAndInterestsTabIndex = this.tabs.findIndex((tab) => {
      return tab.id === "skillsAndInterests";
    });

    if (this.contactIsVolunteer && skillsAndInterestsTabIndex === -1) {
      this.tabs.push({
        id: "skillsAndInterests",
        title: "Skills and Interests",
        selected: false,
        status: "",
      });
    } else if (!this.contactIsVolunteer && skillsAndInterestsTabIndex > -1) {
      this.tabs.splice(skillsAndInterestsTabIndex, 1);
    }

    const schedulesTabIndex = this.tabs.findIndex((tab) => {
      return tab.id === "schedules";
    });

    if (this.contactIsVolunteer && schedulesTabIndex === -1) {
      this.tabs.push({
        id: "schedules",
        title: "Schedule",
        selected: false,
        status: "",
      });
    } else if (!this.contactIsVolunteer && schedulesTabIndex > -1) {
      this.tabs.splice(schedulesTabIndex, 1);
    }
  }

  // Recheck NPI from Job Role (Async Validation)
  recheckJobRole() {
    this.jobRoleControl.updateValueAndValidity();
    this.checkCanSave();
  }

  // Account (System User)
  addAccountFormControl() {
    if (!this.parentForm.contains("account")) {
      this.parentForm.addControl(
        "account",
        this.fb.control(null, Validators.required)
      );
    }
  }

  removeAccountFormControl() {
    if (this.parentForm.contains("account")) {
      this.parentForm.removeControl("account");
    }
  }

  // Delete Phone Number, Address and Credential
  deletePhoneNumber(phoneNumber: any) {
    this.deletedPhoneNumbers.push(phoneNumber);
  }

  deleteAddress(address: any) {
    this.deletedAddresses.push(address);
  }

  deleteCredential(credential: any) {
    this.deletedCredentials.push(credential);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.ngOnDestroy();
  }
}



// WEBPACK FOOTER //
// ./src/pages/company-resources/company-resources-contact/company-resources-contact.ts